'use client'

import { useEffect } from 'react'

import { useMergedNotifications } from '~/app/notifications/useMergedNotifications'

import type { FleetHealthLevel, SynopDesktopBridge } from './bridge'

/**
 * Drives the desktop shell's menu-bar tray dot and dock badge from the same
 * notification feed the in-app bell reads (Workflows in-app notifications).
 *
 * Polls with `ignoreFocus` so the tray keeps updating while the window is
 * unfocused or hidden — the whole point of a menu-bar presence. Level maps
 * from the highest unread priority: Critical → critical (red), High/Medium →
 * warning (amber), otherwise ok (green). Count is the server-side unread
 * total.
 */
export default function useDesktopFleetStatus(bridge: SynopDesktopBridge): void {
  const { rows, unreadCount, isLoading, hasFetchError } = useMergedNotifications({
    scope: 'bell',
    unreadOnly: true,
    intervalSeconds: 60,
    ignoreFocus: true
  })

  useEffect(() => {
    if (isLoading) return
    const level = hasFetchError
      ? 'unknown'
      : resolveLevel(
          rows.map((row) => row.priority),
          unreadCount
        )
    void bridge.setFleetHealth?.({ level, count: unreadCount })
    void bridge.setBadgeCount(unreadCount)
  }, [bridge, hasFetchError, rows, unreadCount, isLoading])
}

function resolveLevel(unreadPriorities: string[], unreadCount: number): FleetHealthLevel {
  if (unreadPriorities.includes('Critical')) return 'critical'
  if (unreadPriorities.includes('High') || unreadPriorities.includes('Medium')) return 'warning'
  // Unread total comes from server-side counts; severity only inspects the
  // fetched page — treat any remaining unread as at least warning.
  if (unreadCount > 0) return 'warning'
  return 'ok'
}
